import { type CookieConsentResult, NonEssentialVendors } from '../types/cookieConsent';

declare global {
  interface Window {
    AccessCookiePolicies?: { SDK?: CookiePoliciesSDK };
  }
}

interface CookiePoliciesSDK {
  tabInitialise: (options: { config: CookieConsentConfig }) => Promise<ConsentResponse>;
}

interface CookieConsentConfig {
  policies: string[];
  policyVersion: string;
  applicationId: string;
  policyUrl: string;
  parties: Party[];
}

interface Party {
  policy: string;
  vendors: Vendor[];
}

interface Vendor {
  name: string;
  description: string;
  vendorUrl: string;
  partyType: string;
}

interface ConsentResponse {
  consent: string | ConsentDetail[];
}

interface ConsentDetail {
  policy: string;
  vendors: string[];
}

const SDK_URL = 'https://cookie.access-evo.com/consent/access-cookie-policies-js/';

function buildVendorCatalog(policyUrl: string) {
  return {
    essential: [
      {
        name: 'Intercom',
        description: 'A customer communication platform we use to provide in-app chat support and help resources',
        vendorUrl: 'https://www.intercom.com/legal/privacy',
        partyType: 'First Party',
      },
      {
        name: 'Session Storage - Authentication',
        description: 'Stores authentication tokens during your session to keep you securely signed in',
        vendorUrl: policyUrl,
        partyType: 'First Party',
      },
      {
        name: 'Session Storage - Navigation State',
        description:
          'Preserves your navigation context (e.g., deep links to specific customers or disputes) during sign-in',
        vendorUrl: policyUrl,
        partyType: 'First Party',
      },
      {
        name: 'Session Storage - Company Selection',
        description: "Remembers your selected company during a session so you don't need to reselect it",
        vendorUrl: policyUrl,
        partyType: 'First Party',
      },
      {
        name: 'Session Storage - Onboarding',
        description: 'Tracks your onboarding progress during a session',
        vendorUrl: policyUrl,
        partyType: 'First Party',
      },
      {
        name: 'Local Storage - User Preferences',
        description:
          'Stores your column layouts, filter preferences, sidebar state, dashboard settings, and table sort preferences so the app remembers your choices',
        vendorUrl: policyUrl,
        partyType: 'First Party',
      },
    ],
    analytics: [
      {
        name: NonEssentialVendors.Wootric,
        description: 'Collects Net Promoter Score feedback to improve user experience',
        vendorUrl: 'https://wootric.com/privacy-policy/',
        partyType: 'Third Party',
      },
      {
        name: NonEssentialVendors.FeedbackCollector,
        description: 'Collects feature-level feedback to help us understand which areas of the product to improve',
        vendorUrl: policyUrl,
        partyType: 'First Party',
      },
    ],
  };
}

const buildParties = (vendorCatalog: ReturnType<typeof buildVendorCatalog>): Party[] =>
  Object.entries(vendorCatalog).map(([policy, vendors]) => ({
    policy: policy === 'essential' ? 'essential & security' : policy,
    vendors: vendors.map((v) => ({
      name: v.name,
      description: v.description,
      vendorUrl: v.vendorUrl,
      partyType: v.partyType,
    })),
  }));

function waitForSdk(timeoutMs = 10000, intervalMs = 50): Promise<CookiePoliciesSDK> {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const check = () => {
      const sdk = window.AccessCookiePolicies?.SDK;
      if (sdk) {
        resolve(sdk);
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        reject(new Error('AccessCookiePolicies SDK did not load within timeout'));
        return;
      }
      setTimeout(check, intervalMs);
    };
    check();
  });
}

function mapVendorConsent(
  vendorCatalog: ReturnType<typeof buildVendorCatalog>,
  consent: ConsentResponse,
): CookieConsentResult {
  if (consent?.consent === '*') {
    return Object.fromEntries(vendorCatalog.analytics.map((v) => [v.name, true])) as CookieConsentResult;
  }

  const allowedVendors = new Set<string>();
  if (Array.isArray(consent?.consent)) {
    for (const entry of consent.consent) {
      if (entry.policy === 'essential & security') continue;
      for (const vendor of entry.vendors) {
        allowedVendors.add(vendor);
      }
    }
  }

  return Object.fromEntries(
    vendorCatalog.analytics.map((v) => [v.name, allowedVendors.has(v.name)]),
  ) as CookieConsentResult;
}

export async function loadCookieSdk(applicationId: string, policyUrl: string): Promise<CookieConsentResult> {
  const vendorCatalog = buildVendorCatalog(policyUrl);

  if (window.location.hostname === 'localhost') {
    return mapVendorConsent(vendorCatalog, { consent: '*' });
  }

  if (!document.querySelector(`script[src="${SDK_URL}"]`)) {
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    document.head.appendChild(script);
  }

  const sdk = await waitForSdk();

  const consent = await sdk.tabInitialise({
    config: {
      policies: ['essential', 'analytics'],
      policyVersion: '2025-05-16',
      applicationId,
      policyUrl,
      parties: buildParties(vendorCatalog),
    },
  });

  return mapVendorConsent(vendorCatalog, consent);
}
